/**
 * Ham kullanıcı girdisi ⇄ sözleşmedeki `Decimal` biçimi arasındaki TEK dönüşüm
 * noktası. Başka hiçbir dosya `parseFloat`, `Number()` ya da elle `replace`
 * yapmaz; para değeri hiçbir zaman `number` tipine düşmez.
 *
 * Sözleşme (`packages/contracts/src/local-api.ts`):
 *   DECIMAL_PATTERN = /^-?\d{1,18}(\.\d{1,12})?$/
 * yani nokta ondalık ayırıcı, en fazla 18 tam + 12 ondalık hane.
 *
 * Türkçe girdi beklentisi: binlik `.`, ondalık `,`. Ama kullanıcı numpad'den
 * `.` de yazabilir, yapıştırdığı metinde boşluk da olabilir. Bu yüzden girdi
 * tarafı esnek, çıktı tarafı katıdır: dönüşüm sonucu `isValidDecimal` ile
 * doğrulanır ve geçmezse hata döner — sessiz kabul yok.
 */

import { isValidDecimal } from "@parafon/contracts/local-api";
import type { Decimal } from "@parafon/contracts";
import { M, YEREL } from "../dil";
import { MASKE, tutarlarGizliMi } from "./tutarGizleme";

export type DecimalParseError =
  | "EMPTY" // boş / yalnızca boşluk
  | "BAD_CHARS" // rakam ve ayırıcı dışında karakter
  | "BAD_GROUPING" // "1.23.456" gibi tutarsız binlik gruplama
  | "AMBIGUOUS" // birden fazla ondalık ayırıcı adayı ("1,2,3")
  | "TOO_MANY_DECIMALS" // 12 haneden fazla ondalık
  | "TOO_LARGE" // 18 haneden fazla tam kısım
  | "INVALID"; // sözleşme desenine uymadı (son savunma hattı)

export type DecimalParseResult =
  | {
      readonly ok: true;
      readonly value: Decimal;
      /**
       * İki türlü okunabilen yazım: tek ayırıcı ve ardından tam üç hane
       * ("1.234", "1,234"). Kullanıcı kararı 27.10.2026 (seçenek c): giriş
       * dilin kuralıyla okunur, böyle bir yazımda nasıl okunduğu HER ZAMAN
       * alanın altında gösterilir.
       */
      readonly belirsiz?: true;
    }
  | { readonly ok: false; readonly error: DecimalParseError };

/** Girdinin ondalık ayırıcı kuralı: dilin yereli. */
export type OndalikKurali = "," | ".";

/** Bu sayfa yüklemesinin ondalık ayırıcısı (Türkçe ",", İngilizce "."). */
export const ONDALIK: OndalikKurali = yerelOndalik(YEREL);

function yerelOndalik(yerel: string): OndalikKurali {
  try {
    const p = new Intl.NumberFormat(yerel).formatToParts(1.5).find((x) => x.type === "decimal");
    return p?.value === "." ? "." : ",";
  } catch {
    return ",";
  }
}

/** Girdi temizliğinde atılan boşluk türleri (yapıştırılan metinde çıkar). */
const STRIPPED_SPACES = /[\s    ]/g;
/** Bazı klavyeler/panolar Unicode eksi ve tam genişlikli işaret üretir. */
const MINUS_VARIANTS = /^[-−‒–－]/;
const PLUS_VARIANTS = /^[+＋]/;

const MAX_INT_DIGITS = 18;
const MAX_FRAC_DIGITS = 12;

const err = (error: DecimalParseError): DecimalParseResult => ({ ok: false, error });

/**
 * Ham girdiyi `Decimal`'e çevirir.
 *
 * Ayırıcı kararı (belirsizliği tahmin etmek yerine kurala bağlıyoruz):
 *  - Hem `,` hem `.` varsa → SONRA gelen ondalık, öteki binliktir.
 *  - Yalnızca `,` varsa → ondalıktır ("1,5" → "1.5").
 *  - Yalnızca `.` varsa ve BİRDEN FAZLA ise → binliktir ("1.234.567" → 1234567).
 *  - Yalnızca tek `.` varsa → ONDALIKTIR. Yani "1.234" = 1,234 (bir tam 234).
 *    Bu bilinçli bir karar: numpad'den gelen en yaygın girdi budur. Arayüz
 *    girdi alanından çıkarken anlaşılan değeri dilin biçiminde geri yazar,
 *    böylece kullanıcı yanlış anlaşılmayı görür.
 *
 * Ondalığı "." olan dilde (İngilizce) tek farkı tek virgül: ardından tam üç
 * hane geliyorsa binliktir ("1,234" → 1234), değilse ondalık ("1,5" → 1.5;
 * geri yazılan "1.5" bunu gösterir).
 */
export function parseDecimalInput(raw: string, kural: OndalikKurali = ONDALIK): DecimalParseResult {
  if (typeof raw !== "string") return err("BAD_CHARS");

  let s = raw.replace(STRIPPED_SPACES, "");
  if (s === "") return err("EMPTY");

  let negative = false;
  if (MINUS_VARIANTS.test(s)) {
    negative = true;
    s = s.slice(1);
  } else if (PLUS_VARIANTS.test(s)) {
    s = s.slice(1);
  }
  if (s === "") return err("EMPTY");

  if (/[^0-9.,]/.test(s)) return err("BAD_CHARS");

  const commas = countOf(s, ",");
  const dots = countOf(s, ".");

  let intRaw: string;
  let fracRaw = "";
  let groupedWith: "." | "," | null = null;

  if (commas > 0 && dots > 0) {
    const decSep = s.lastIndexOf(",") > s.lastIndexOf(".") ? "," : ".";
    const groupSep = decSep === "," ? "." : ",";
    if (countOf(s, decSep) > 1) return err("AMBIGUOUS");
    const cut = s.lastIndexOf(decSep);
    intRaw = s.slice(0, cut);
    fracRaw = s.slice(cut + 1);
    groupedWith = groupSep;
  } else if (commas > 1) {
    // "1,234,567" — İngilizce binlik gruplama. Kabul ediyoruz, ondalık yok.
    intRaw = s;
    groupedWith = ",";
  } else if (commas === 1 && kural === "." && /^\d{1,3},\d{3}$/.test(s)) {
    intRaw = s;
    groupedWith = ",";
  } else if (commas === 1) {
    const cut = s.indexOf(",");
    intRaw = s.slice(0, cut);
    fracRaw = s.slice(cut + 1);
  } else if (dots > 1) {
    intRaw = s;
    groupedWith = ".";
  } else if (dots === 1) {
    const cut = s.indexOf(".");
    intRaw = s.slice(0, cut);
    fracRaw = s.slice(cut + 1);
  } else {
    intRaw = s;
  }

  if (groupedWith !== null) {
    if (!isValidGrouping(intRaw, groupedWith)) return err("BAD_GROUPING");
    intRaw = intRaw.split(groupedWith).join("");
  }

  // Ondalık ayırıcı seçildikten sonra hiçbir tarafta ayırıcı kalmamalı.
  if (/[.,]/.test(intRaw) || /[.,]/.test(fracRaw)) return err("BAD_GROUPING");
  if (intRaw === "" && fracRaw === "") return err("EMPTY");
  if (!/^\d*$/.test(intRaw) || !/^\d*$/.test(fracRaw)) return err("BAD_CHARS");

  if (fracRaw.length > MAX_FRAC_DIGITS) return err("TOO_MANY_DECIMALS");

  const intPart = stripLeadingZeros(intRaw);
  if (intPart.length > MAX_INT_DIGITS) return err("TOO_LARGE");

  const magnitude = fracRaw === "" ? intPart : `${intPart}.${fracRaw}`;
  const isZero = /^0(\.0*)?$/.test(magnitude);
  const value = negative && !isZero ? `-${magnitude}` : magnitude;

  // Son savunma: sözleşmenin kendi doğrulayıcısı.
  if (!isValidDecimal(value)) return err("INVALID");
  const belirsiz = commas + dots === 1 && /^\d{1,3}[.,]\d{3}$/.test(s);
  return belirsiz ? { ok: true, value, belirsiz: true } : { ok: true, value };
}

/**
 * Girdi alanlarının kullandığı kısa yol: geçersizse `null`.
 * Sıfır ile "boş" karıştırılmasın diye boş girdi de `null` döner.
 */
export function toDecimalOrNull(raw: string): Decimal | null {
  const r = parseDecimalInput(raw);
  return r.ok ? r.value : null;
}

export const DECIMAL_ERROR_TEXT: Readonly<Record<DecimalParseError, string>> = M.bicim.sayiHatasi;

// ---------------------------------------------------------------------------
// Görüntüleme yönü: Decimal → dilin biçimi
// ---------------------------------------------------------------------------

export interface FormatDecimalOptions {
  /** Sabit ondalık hane sayısı. Verilmezse değerin kendi hanesi korunur. */
  readonly fractionDigits?: number;
  /** En fazla gösterilecek ondalık hane (fractionDigits verilmediğinde). */
  readonly maxFractionDigits?: number;
  /** Pozitif değerlerin önüne "+" koy (delta gösterimleri için). */
  readonly signed?: boolean;
  /** "Tutarları gizle" bu sayıyı maskelemez (yüzde, pay, giriş alanı). */
  readonly gizlenmez?: boolean;
}

/** Miktarın gösterilen en fazla ondalık hanesi: defterin sakladığı kadar (cihaz şeması v7). */
export const MIKTAR_HANE = 9;

/**
 * `Decimal`'i dilin yerel biçiminde yazar (Türkçe: binlik nokta, ondalık
 * virgül; İngilizce: binlik virgül, ondalık nokta).
 *
 * Intl.NumberFormat (V3) string girdiyi tam duyarlıkla biçimlendirir, bu yüzden
 * değer `number`'a çevrilmez. Ortam desteklemiyorsa elle gruplamaya düşer —
 * her iki yol da testli.
 */
export function formatDecimal(value: Decimal | null | undefined, options: FormatDecimalOptions = {}): string {
  if (value === null || value === undefined || value === "") return "—";
  if (!isValidDecimal(value)) return "?";
  // Tutarları gizle (02.11.2026): maske anormallik değil; "—" ve "?" yukarıda kalır.
  if (options.gizlenmez !== true && tutarlarGizliMi()) return MASKE;

  const { fractionDigits, maxFractionDigits, signed = false } = options;
  const ownFraction = value.includes(".") ? (value.split(".")[1] ?? "").length : 0;
  const min = fractionDigits ?? 0;
  const max = fractionDigits ?? Math.min(Math.max(ownFraction, min), maxFractionDigits ?? MAX_FRAC_DIGITS);

  let body: string;
  try {
    const nf = new Intl.NumberFormat(YEREL, {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
      useGrouping: true,
    });
    // Intl V3 string girdiyi tam duyarlıkla biçimlendirir; TS'in `format`
    // imzası ortama göre `number | bigint` kalabildiği için burada daraltıyoruz.
    const formatExact = nf.format as (v: string) => string;
    body = formatExact(value);
    if (!/\d/.test(body)) throw new Error("Intl string girdisini biçimlendirmedi");
  } catch {
    body = manualFormat(value, min, max);
  }

  if (signed && !value.startsWith("-") && !isZeroDecimal(value)) return `+${body}`;
  return body;
}

/** Para hücreleri: değer + ayrı bir para birimi kodu. */
export function formatMoney(
  value: Decimal | null | undefined,
  options: FormatDecimalOptions = {},
): string {
  return formatDecimal(value, { fractionDigits: 2, ...options });
}

/**
 * ORAN METNİ — ondalık kesri yüzdeye çevirip biçimlendirir.
 *
 * Yüzde ÇARPANI burada ve YALNIZCA burada: sözleşme oranı kesir olarak
 * taşıyor ("0.123400"), ekran yüzde olarak gösteriyor. İkisi arasındaki 100
 * çarpanının tek bir yerde durması, bir yerde çarpılıp başka yerde
 * unutulmasını imkânsız kılıyor.
 *
 * İşaret her zaman yazılıyor ("+%12,34"): bir getiri sayısında işaretin
 * varsayılan sayılması, zararın kâr gibi okunmasının en kısa yoludur.
 */
export function formatRatio(value: Decimal | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  if (!isValidDecimal(value)) return "?";
  // İŞARET YÜZDENİN ÖNÜNDE: Türkçede yüzde işareti sayının önüne yazılır
  // ("%100"), dolayısıyla artı/eksi de onun önüne gelir — "+%100,00".
  // "%+100,00" okunurken işaret yüzdenin parçası gibi görünüyordu.
  // Yüzde işaretinin yeri dile göre: Türkçe "+%12,34", İngilizce "+12.34%".
  const govde = formatDecimal(shiftTwo(value), { fractionDigits: 2, signed: true, gizlenmez: true });
  const eksi = govde.startsWith("-");
  const arti = govde.startsWith("+");
  const cipl = eksi || arti ? govde.slice(1) : govde;
  return `${eksi ? "-" : arti ? "+" : ""}${M.bicim.yuzde(cipl)}`;
}

/**
 * Ondalık noktayı iki basamak SAĞA kaydırır — kesirden yüzdeye.
 *
 * Çarpma değil kaydırma, çünkü kaydırma kayıpsız ve yuvarlamasız: "0.123456"
 * → "12.3456". 100 ile çarpan bir kayan noktalı işlem burada son basamağı
 * bozabilirdi ve oran zaten bir bölmenin sonucu — iki yuvarlama üst üste
 * binerdi.
 */
function shiftTwo(value: Decimal): Decimal {
  const negative = value.startsWith("-");
  const body = negative ? value.slice(1) : value;
  const [intPart = "0", fracPart = ""] = body.split(".");
  const frac = fracPart.padEnd(2, "0");
  const head = (intPart + frac.slice(0, 2)).replace(/^0+(?=\d)/, "");
  const tail = frac.slice(2);
  return `${negative ? "-" : ""}${head}${tail === "" ? "" : `.${tail}`}`;
}

/** Oranın rengi — kazanç/zarar. Sıfırsa nötr. */
export function ratioSign(value: Decimal | null | undefined): string {
  const s = decimalSign(value);
  return s > 0 ? "text-gain" : s < 0 ? "text-loss" : "text-muted";
}

export function isZeroDecimal(value: Decimal): boolean {
  return /^-?0(\.0*)?$/.test(value);
}

export function decimalSign(value: Decimal | null | undefined): -1 | 0 | 1 {
  if (!value || !isValidDecimal(value)) return 0;
  if (isZeroDecimal(value)) return 0;
  return value.startsWith("-") ? -1 : 1;
}

// ---------------------------------------------------------------------------
// Toplama — `positions()` benzeri türetimler için tam sayı tabanlı ondalık
// ---------------------------------------------------------------------------

const SCALE = MAX_FRAC_DIGITS;

function toScaled(value: Decimal): bigint {
  const negative = value.startsWith("-");
  const body = negative ? value.slice(1) : value;
  const [intPart = "0", fracPart = ""] = body.split(".");
  const frac = fracPart.padEnd(SCALE, "0").slice(0, SCALE);
  const n = BigInt(intPart + frac);
  return negative ? -n : n;
}

function fromScaled(n: bigint): Decimal {
  const negative = n < 0n;
  const digits = (negative ? -n : n).toString().padStart(SCALE + 1, "0");
  const intPart = stripLeadingZeros(digits.slice(0, digits.length - SCALE));
  const fracPart = digits.slice(digits.length - SCALE).replace(/0+$/, "");
  const magnitude = fracPart === "" ? intPart : `${intPart}.${fracPart}`;
  return negative && !/^0(\.0*)?$/.test(magnitude) ? `-${magnitude}` : magnitude;
}

/** Kayan noktaya düşmeden toplar. Geçersiz değerler atlanmaz, hata atar. */
export function decimalSum(values: Iterable<Decimal>): Decimal {
  let acc = 0n;
  for (const v of values) {
    if (!isValidDecimal(v)) throw new Error(`Geçersiz Decimal: ${v}`);
    acc += toScaled(v);
  }
  return fromScaled(acc);
}

export function decimalNegate(value: Decimal): Decimal {
  if (!isValidDecimal(value)) throw new Error(`Geçersiz Decimal: ${value}`);
  return fromScaled(-toScaled(value));
}

export function decimalCompare(a: Decimal, b: Decimal): -1 | 0 | 1 {
  const sa = toScaled(a);
  const sb = toScaled(b);
  return sa === sb ? 0 : sa > sb ? 1 : -1;
}

// ---------------------------------------------------------------------------

function countOf(s: string, ch: string): number {
  let n = 0;
  for (const c of s) if (c === ch) n += 1;
  return n;
}

function isValidGrouping(intRaw: string, sep: "." | ","): boolean {
  if (!intRaw.includes(sep)) return /^\d*$/.test(intRaw);
  const parts = intRaw.split(sep);
  const head = parts[0] ?? "";
  if (!/^\d{1,3}$/.test(head)) return false;
  return parts.slice(1).every((p) => /^\d{3}$/.test(p));
}

function stripLeadingZeros(digits: string): string {
  const trimmed = digits.replace(/^0+/, "");
  return trimmed === "" ? "0" : trimmed;
}

/** Intl'in string girdiyi desteklemediği ortamlar için yedek yol. */
function manualFormat(value: Decimal, min: number, max: number): string {
  const negative = value.startsWith("-");
  const body = negative ? value.slice(1) : value;
  const [intRaw = "0", fracRaw = ""] = body.split(".");
  const rounded = roundFraction(intRaw, fracRaw, max);
  const frac = rounded.frac.padEnd(min, "0");
  const ondalik = ONDALIK;
  const binlik = ondalik === "," ? "." : ",";
  const grouped = rounded.int.replace(/\B(?=(\d{3})+(?!\d))/g, binlik);
  const out = frac === "" ? grouped : `${grouped}${ondalik}${frac}`;
  return negative ? `-${out}` : out;
}

function roundFraction(intPart: string, frac: string, max: number): { int: string; frac: string } {
  if (frac.length <= max) return { int: intPart, frac };
  const keep = frac.slice(0, max);
  const nextDigit = Number(frac[max] ?? "0");
  if (nextDigit < 5) return { int: intPart, frac: keep };
  const bumped = (BigInt(intPart + keep) + 1n).toString().padStart(intPart.length + max, "0");
  return {
    int: stripLeadingZeros(bumped.slice(0, bumped.length - max)),
    frac: max === 0 ? "" : bumped.slice(bumped.length - max),
  };
}

/**
 * SATIŞIN HESABA GEÇEN NET TUTARI — "Parayı çektim" (kullanıcı kararı
 * 19.10.2026) aynı gün bu tutarda bir çekme yazar. Motorun kuralıyla BİREBİR:
 * brüt = miktar (9 hane) × fiyat (12 hane), 12 haneye yarım yukarı yuvarlanır
 * (`moneyOfQtyPrice`); masraf ve vergi (8 hane) çıkarılır. Tamsayı aritmetiği
 * — para `number` ile hesaplanmaz. Çıkan çekme satırı satışın nakit etkisini
 * kuruşu kuruşuna sıfırlar. Okunamayan girdi ya da eksi sonuç: null.
 */
export function satisNetTutari(quantity: Decimal, price: Decimal, fee?: Decimal, tax?: Decimal): Decimal | null {
  const olcekli = (v: string, hane: number): bigint | null => {
    const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(v.trim());
    if (m === null) return null;
    const kesir = m[3] ?? "";
    if (kesir.length > hane) return null;
    const b = BigInt(`${m[2]}${kesir.padEnd(hane, "0")}`);
    return m[1] === "-" ? -b : b;
  };
  const q = olcekli(quantity, 9);
  const p = olcekli(price, 12);
  const f = olcekli(fee ?? "0", 8);
  const t = olcekli(tax ?? "0", 8);
  if (q === null || p === null || f === null || t === null) return null;
  const urun = q * p; // 21 hane
  const bolen = 10n ** 9n;
  const brut = urun >= 0n ? (urun + bolen / 2n) / bolen : -((-urun + bolen / 2n) / bolen);
  const net = brut - f * 10_000n - t * 10_000n;
  if (net <= 0n) return null;
  const metin = net.toString().padStart(13, "0");
  const tam = metin.slice(0, -12);
  const kes = metin.slice(-12).replace(/0+$/, "");
  return kes === "" ? tam : `${tam}.${kes}`;
}
